import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import { makeStyles } from "@material-ui/styles";
import { AxiosError } from "axios";
import { FormikProps } from "formik";
import React from "react";
import { useHistory } from "react-router-dom";
import { useSpring } from "react-spring";
import TestRequestResponse from "../../../api/models/test-request/TestRequestResponse";
import { TestRequestPermissions } from "../../../api/services/PermissionApi";
import TestRequestApi from "../../../api/services/TestRequestApi";
import CustomButton from "../../../components/Button/CustomButton";
import InfoBox from "../../../components/InfoBox";
import { StyledMenu } from "../../../components/Menu/StyledMenu";
import { SnackbarVisibility } from "../../../contexts/ErrorSnackbarContext";
import useEnv from "../../../hooks/env";
import { HttpErrorDataType, HttpErrorType } from "../../../models/HttpErrorType";
import { tesaDarkRedError, tesaGreenPositive } from "../../../styles/colors";
import { fadeOut } from "../../../styles/springs";
import { EditRequestNameDialogProps } from "../../Dashboard/EditRequestNameDialog";
import { infoAssignToRequest, infoIsAssignedToRequest, infoRequestCreation } from "../messages";
import AcceptTestRequestButton from "./AcceptTestRequestButton";
import ApproveTestRequestButton from "./ApproveTestRequestButton";
import DescriptionFormType from "./models/Description.form";
import { mapToUpdateRequest } from "./models/Description.request-mapper";
import { TEST_REQUEST_ACTION_DICTIONARY } from "./models/TestRequestAction.model";
import SubmitTestRequestButton from "./SubmitTestRequestButton";
import TestRequestActionsMenuItem from "./TestRequestActionsMenuItem";
import TestRequestLifecycleApi from "../../../api/services/TestRequestLifecycleApi";

type InfoType = "success" | "info" | "warning" | "error";
type ConfirmationOverride = {
  [K in keyof DescriptionFormType]?: boolean;
};

const useStyles = makeStyles({
  root: {
    display: "block",
    margin: "0 auto",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
  successMessage: { color: tesaGreenPositive },
  errorMessage: { color: tesaDarkRedError },
  moreVertIcon: {
    height: "14px",
  },
});

interface DescriptionFormButtonsProps {
  createMode: boolean;
  formikProps: FormikProps<DescriptionFormType>;
  newOrDraft: boolean;
  permissions: TestRequestPermissions & { initiallyLoaded: boolean };
  testRequestObj: any;
  testRequestId: string;
  testRequestTitleSuffix: string;
  handleDirtyChange: (resetFlag?: boolean) => void;
  handleHttpError: (error?: HttpErrorType, snackbarVisibility?: SnackbarVisibility) => void;
  handleSave: (
    values: DescriptionFormType,
    refresh?: boolean | undefined,
    confirmationOverride?: ConfirmationOverride,
  ) => Promise<TestRequestResponse | void>;
  isHighConfidentialityRequired: (error: AxiosError<HttpErrorDataType>) => boolean;
  preSave: (formVals: DescriptionFormType) => () => Promise<void | TestRequestResponse>;
  refreshDescriptionTab: (testReqData: TestRequestResponse, redirect?: boolean) => Promise<void>;
  scrollToError: () => void;
  setIsAssignDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  showEditRequestNameDialog: (doShow: boolean, props?: EditRequestNameDialogProps) => Promise<string | void>;
  showSnackbar: (text: string, type?: InfoType) => void;
  updateTestRequestTitleSuffix: (newTestRequestTitleSuffix: string) => void;
  validateForm: (ensureHighlyConfidential?: boolean) => Promise<void>;
}

const DescriptionFormButtons: React.FC<DescriptionFormButtonsProps> = ({
  createMode,
  formikProps,
  handleDirtyChange,
  handleSave,
  handleHttpError,
  isHighConfidentialityRequired,
  newOrDraft,
  permissions,
  preSave,
  refreshDescriptionTab,
  scrollToError,
  setIsAssignDialogOpen,
  showEditRequestNameDialog,
  showSnackbar,
  testRequestId,
  testRequestObj,
  testRequestTitleSuffix,
  updateTestRequestTitleSuffix,
  validateForm,
}) => {
  const { api } = useEnv();
  const testRequestApi = new TestRequestApi(api);
  const testRequestLifecycleApi = new TestRequestLifecycleApi(api);
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const { isSubmitting, isValid, setValues, submitForm, values } = formikProps;

  const springPropsFadeOut = useSpring(fadeOut);

  const {
    description: descriptionPermissions,
    submit: showSubmitButton,
    accept,
    approve: showApproveButton,
    askForApproval,
    askForReview: showReviewButton,
    assignToSelf,
    assignToTeamMember,
    decline: showDeclineButton,
    delete: showDeleteButton,
    cancel: showCancelButton,
    duplicate: showDuplicateButton,
    reopen: showReopenButton,
    recall: showRecallButton,
  } = permissions;
  const showActionsMenu =
    showCancelButton ||
    showDeleteButton ||
    showDeclineButton ||
    showDuplicateButton ||
    showReviewButton ||
    showReopenButton;
  const showAcceptButton = accept || askForApproval;
  const showAssignButton = assignToSelf || assignToTeamMember;
  const showSendForApprovalButton = testRequestObj?.state === "FOR_EVALUATION";

  const history = useHistory();
  const queryParams = new URLSearchParams(window.location.search);
  const opportunityId = queryParams.get("opportunityId") || "";
  const requestType = queryParams.get("requestType") || "";

  const handleContextMenuClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleContextMenuClose = (): void => {
    setAnchorEl(null);
  };

  const preSaveWithVals = preSave(values);

  return (
    <>
      <Grid container direction="row" justify="flex-end">
        <InfoBox
          compress
          flipChildren
          text={createMode ? infoRequestCreation : showAssignButton ? infoAssignToRequest : infoIsAssignedToRequest}
        >
          <div className={classes.buttonContainer}>
            {showActionsMenu && (
              <CustomButton
                onClick={handleContextMenuClick}
                text="Actions"
                startIcon={<MoreVertIcon className={classes.moreVertIcon} />}
              />
            )}
            {descriptionPermissions?.editDescription &&
              (newOrDraft ? (
                <>
                  <Box width={24} />
                  <CustomButton
                    primary
                    disabled={isSubmitting}
                    text="Save"
                    onClick={() => handleSave(values).catch(() => scrollToError())}
                  />
                </>
              ) : (
                <>
                  <Box width={24} />
                  <CustomButton
                    primary
                    disabled={isSubmitting}
                    text="Save"
                    onClick={() => {
                      submitForm();
                      if (!isValid) {
                        setTimeout(scrollToError, 100);
                      }
                    }}
                  />
                </>
              ))}
            {showSubmitButton && (
              <>
                <Box width={24} />
                <SubmitTestRequestButton
                  disabled={isSubmitting}
                  handleShowSuccessMessage={() => {
                    showSnackbar("Successfully submitted.", "success");
                  }}
                  highConfidentialityRequired={() => {
                    return testRequestApi
                      .validateTestRequestDescription({
                        ...mapToUpdateRequest(testRequestTitleSuffix, values),
                        crmOpportunityId: opportunityId,
                        tesaRequestType: +requestType,
                      })
                      .then(() => false)
                      .catch((e: AxiosError<HttpErrorDataType>) => {
                        if (e.response?.status === 400) {
                          return isHighConfidentialityRequired(e);
                        }
                        handleHttpError(e);
                        throw e;
                      });
                  }}
                  onSuccess={(newTestReqObj) => refreshDescriptionTab(newTestReqObj, true)}
                  onSubmitClick={(setHighlyConfidential, newTitle) => {
                    if (newTitle) {
                      updateTestRequestTitleSuffix(newTitle);
                    }
                    return validateForm(setHighlyConfidential)
                      .then(() =>
                        handleSave(
                          {
                            ...values,
                            projectConfidentiality: setHighlyConfidential
                              ? "HIGHLY_CONFIDENTIAL"
                              : values.projectConfidentiality,
                            title: newTitle || values.title,
                          },
                          false,
                        ),
                      )
                      .catch((error) => {
                        setValues({
                          ...values,
                          projectConfidentiality: setHighlyConfidential
                            ? "HIGHLY_CONFIDENTIAL"
                            : values.projectConfidentiality,
                          title: newTitle || values.title,
                        });
                        if (newTitle || setHighlyConfidential) {
                          handleDirtyChange();
                        }
                        if (newTitle) {
                          updateTestRequestTitleSuffix(newTitle);
                        }
                        handleHttpError(error);
                      })
                      .finally(() => {
                        history.push({ ...history, search: queryParams.toString() });
                      });
                  }}
                  requestTitleSuffix={testRequestTitleSuffix}
                  requestTitlePrefix={testRequestObj.titlePrefix}
                  showEditRequestNameDialog={showEditRequestNameDialog}
                  style={springPropsFadeOut}
                />
              </>
            )}
            {showAcceptButton && (
              <>
                <Box width={24} />
                <AcceptTestRequestButton
                  approvalNeeded={!!values.suggestedApprover}
                  disabled={isSubmitting}
                  onSuccess={(testRequest) => {
                    showSnackbar("Successfully accepted.", "success");
                    refreshDescriptionTab(testRequest).catch(handleHttpError);
                  }}
                  preSave={preSaveWithVals}
                  testRequestId={createMode ? "" : testRequestId}
                  testRequestName={testRequestObj.fullTitle}
                  duplicatedFrom={testRequestObj.duplicatedFrom || null}
                  style={springPropsFadeOut}
                />
              </>
            )}
            {showAssignButton && (
              <>
                <Box width={24} />
                <CustomButton text="Assign" primary onClick={() => setIsAssignDialogOpen(true)} />
              </>
            )}
            {showApproveButton && (
              <>
                <Box width={24} />
                <ApproveTestRequestButton
                  disabled={isSubmitting}
                  preSave={preSaveWithVals}
                  onSuccess={refreshDescriptionTab}
                  handleShowSuccessMessage={() => {
                    showSnackbar("Successfully approved.", "success");
                  }}
                  testRequestName={testRequestObj.fullTitle}
                  style={fadeOut}
                />
              </>
            )}
            <StyledMenu
              id="customized-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleContextMenuClose}
            >
              {showCancelButton && (
                <TestRequestActionsMenuItem
                  type="CANCEL"
                  dictionary={TEST_REQUEST_ACTION_DICTIONARY}
                  close={handleContextMenuClose}
                  disabled={isSubmitting}
                  testRequestTitle={testRequestObj.fullTitle}
                  onConfirm={() =>
                    testRequestLifecycleApi.cancelTestRequest(createMode ? "" : testRequestId).then(({ data }) => data)
                  }
                  onSuccessCallback={(data) => {
                    refreshDescriptionTab(data);
                  }}
                />
              )}
              {showDeleteButton && (
                <TestRequestActionsMenuItem
                  type="DELETE"
                  dictionary={TEST_REQUEST_ACTION_DICTIONARY}
                  close={handleContextMenuClose}
                  disabled={isSubmitting}
                  testRequestTitle={testRequestObj.fullTitle}
                  onConfirm={() => testRequestLifecycleApi.deleteTestRequest(testRequestId)}
                  onSuccessCallback={() => {
                    history.push("/");
                  }}
                />
              )}
              {showDeclineButton && (
                <TestRequestActionsMenuItem
                  type="DECLINE"
                  dictionary={TEST_REQUEST_ACTION_DICTIONARY}
                  altDialogHeader="Decline Test Request"
                  altDialogText={<>Decline and notify the requester.</>}
                  close={handleContextMenuClose}
                  disabled={isSubmitting}
                  testRequestTitle={testRequestObj.fullTitle}
                  onConfirm={() =>
                    testRequestLifecycleApi.declineTestRequest(createMode ? "" : testRequestId).then(({ data }) => data)
                  }
                  onSuccessCallback={(data) => {
                    refreshDescriptionTab(data);
                  }}
                  preSave={preSaveWithVals}
                />
              )}
              {showReviewButton && (
                <TestRequestActionsMenuItem
                  type="ASK_FOR_REVIEW"
                  dictionary={TEST_REQUEST_ACTION_DICTIONARY}
                  altDialogHeader="Ask Requester for Review"
                  altDialogText={
                    <>Send back the Test Request to the requester and ask for adjustments to the Test Request.</>
                  }
                  altLabel="Ask for Review"
                  close={handleContextMenuClose}
                  disabled={isSubmitting}
                  testRequestTitle={testRequestObj.fullTitle}
                  onConfirm={() =>
                    testRequestLifecycleApi
                      .askForReviewForTestRequest(createMode ? "" : testRequestId)
                      .then(({ data }) => data)
                  }
                  onSuccessCallback={(data) => {
                    refreshDescriptionTab(data);
                  }}
                  preSave={preSaveWithVals}
                />
              )}
              {showSendForApprovalButton && (
                <TestRequestActionsMenuItem
                  type="SEND_FOR_APPROVAL"
                  dictionary={TEST_REQUEST_ACTION_DICTIONARY}
                  altLabel="Send for Approval"
                  altSuccessMessage="The test request was sent for approval."
                  close={handleContextMenuClose}
                  disabled={isSubmitting}
                  testRequestTitle={testRequestObj.fullTitle}
                  onConfirm={() => testRequestLifecycleApi.sendForApproval(testRequestId).then(({ data }) => data)}
                  onSuccessCallback={(data) => {
                    refreshDescriptionTab(data);
                  }}
                  preSave={preSaveWithVals}
                />
              )}
              {showDuplicateButton && (
                <TestRequestActionsMenuItem
                  type="DUPLICATE"
                  dictionary={TEST_REQUEST_ACTION_DICTIONARY}
                  altSuccessMessage="The test request was duplicated (without attachments and comments)."
                  close={handleContextMenuClose}
                  disabled={isSubmitting}
                  testRequestTitle={testRequestObj.fullTitle}
                  onConfirm={() =>
                    testRequestApi.duplicateTestRequest(createMode ? "" : testRequestId).then(({ data }) => data)
                  }
                  onSuccessCallback={(data) => {
                    history.push(`/requests/${data.requestId}`);
                  }}
                  preSave={descriptionPermissions?.editDescription ? preSaveWithVals : undefined}
                />
              )}
              {showReopenButton && (
                <TestRequestActionsMenuItem
                  type="REOPEN"
                  dictionary={TEST_REQUEST_ACTION_DICTIONARY}
                  close={handleContextMenuClose}
                  disabled={isSubmitting}
                  testRequestTitle={testRequestObj.fullTitle}
                  onConfirm={() => testRequestLifecycleApi.reopenTestRequest(testRequestId).then(({ data }) => data)}
                  onSuccessCallback={(data) => {
                    refreshDescriptionTab(data);
                  }}
                />
              )}
              {showRecallButton && (
                <TestRequestActionsMenuItem
                  type="RECALL"
                  altDialogText={
                    <>
                      You are about to re-call a submitted test request and set the request back to status draft. Do you
                      want to proceed?
                    </>
                  }
                  dictionary={TEST_REQUEST_ACTION_DICTIONARY}
                  close={handleContextMenuClose}
                  disabled={isSubmitting}
                  testRequestTitle={testRequestObj.fullTitle}
                  onConfirm={() => testRequestLifecycleApi.recallTestRequest(testRequestId).then(({ data }) => data)}
                  onSuccessCallback={(data) => {
                    refreshDescriptionTab(data);
                  }}
                />
              )}
            </StyledMenu>
          </div>
        </InfoBox>
      </Grid>
    </>
  );
};

export default DescriptionFormButtons;
